import { useEffect, useRef, useState } from 'react';

import type {
  ProfileType,
  PropertyCountRangeType,
  RentalManagerAuthCheckUserInfoWebApiDto,
  ZillowAccountDetailsWebApiDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

export type StepSkipFlags = {
  skipName: boolean;
  skipProfileType: boolean;
  skipPropertyCount: boolean;
};

export const usePrefillUserData = (
  user?: RentalManagerAuthCheckUserInfoWebApiDto,
  accountDetails?: ZillowAccountDetailsWebApiDto,
) => {
  const userProfiles = user && 'profiles' in user ? user.profiles : null;

  // Compute step skip flags once per modal open and freeze them so saving data
  // mid-flow doesn't reshuffle the active steps array
  const stepSkipFlagsRef = useRef<StepSkipFlags | null>(null);
  if (stepSkipFlagsRef.current === null && user !== undefined) {
    stepSkipFlagsRef.current = {
      skipName: Boolean(user?.firstName && user?.lastName),
      skipProfileType: Boolean(userProfiles?.profileType),
      skipPropertyCount: Boolean(userProfiles?.propertyCountRange),
    };
  }
  const stepSkipFlags = stepSkipFlagsRef.current ?? {
    skipName: false,
    skipProfileType: false,
    skipPropertyCount: false,
  };

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedProfileType, setSelectedProfileType] = useState<ProfileType | undefined>(undefined);
  const [propertyCountRange, setPropertyCountRange] = useState<PropertyCountRangeType | undefined>(undefined);

  // Prefill from user data first, then fallback to accountDetails
  useEffect(() => {
    if (user?.firstName) {
      setFirstName(user.firstName);
    } else if (accountDetails?.firstName) {
      setFirstName(accountDetails.firstName);
    }
  }, [user?.firstName, accountDetails?.firstName]);

  useEffect(() => {
    if (user?.lastName) {
      setLastName(user.lastName);
    } else if (accountDetails?.lastName) {
      setLastName(accountDetails.lastName);
    }
  }, [user?.lastName, accountDetails?.lastName]);

  useEffect(() => {
    if (!userProfiles?.profileType && accountDetails?.profileType) {
      setSelectedProfileType(accountDetails.profileType);
    }
  }, [userProfiles?.profileType, accountDetails?.profileType]);

  return {
    firstName,
    setFirstName,
    lastName,
    setLastName,
    selectedProfileType,
    setSelectedProfileType,
    propertyCountRange,
    setPropertyCountRange,
    stepSkipFlags,
  };
};
